class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEvents = this.addEvents.bind(this);

    this.addEvents();

    // Safari bfcache: al restaurar la página desde caché (back/forward), el
    // drawer queda con el DOM viejo (mismo problema documentado en VRC v1.5).
    // Reconstruimos items + footer + badge SIN abrir el drawer.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.rebuildFromEmpty(false);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  /* ==========================================================================
     FIX (Ivan): "drawer muerto tras vaciar el carrito".

     Causa raíz anterior: updateItems() traía cart?view=cart-items, que SOLO
     contiene la tabla de ítems (.js-cart-items-body) — NO el footer con los
     botones ni .js-drawer-total. La lógica exigía encontrar .js-drawer-total
     para salir de is-empty; como no existe en esa vista, la función abortaba
     silenciosamente y el drawer quedaba atascado en is-empty (footer oculto
     por CSS), sin recuperarse al agregar productos.

     Solución: cuando el drawer venía vacío, recuperamos el drawer COMPLETO
     via Section Rendering API (?section_id=cart-drawer), que trae header,
     ítems Y footer con botones. Reemplazamos el interior de #CartDrawer,
     quitamos is-empty de <cart-drawer> y de <cart-drawer-items>, y reabrimos.
     Para el caso normal (drawer que ya tenía ítems) mantenemos el camino
     ligero de cart?view=cart-items.
     ========================================================================== */
  updateItems() {
    // Señales de "hay que reconstruir el drawer completo":
    //  a) el wrapper está marcado vacío, o
    //  b) falta la tabla de items (.js-cart-items-body). Este caso (b) es la
    //     firma del estado inconsistente que produce cart.js: onCartUpdate()
    //     calcula cartIsEmpty leyendo #cartIsEmpty, un nodo que SOLO existe
    //     en /cart; fuera de /cart da false y le quita is-empty al wrapper
    //     aunque el carrito esté vacío. Por eso NO se puede confiar solo en
    //     la clase: la señal dura es la ausencia de la tabla de items.
    const wasEmpty = this.classList.contains('is-empty');
    const hasItemsBody = !!this.querySelector('.js-cart-items-body');

    if (wasEmpty || !hasItemsBody) {
      this.rebuildFromEmpty();
      return;
    }

    fetch(`${window.Shopify.routes.root}cart?view=cart-items`)
      .then((response) => response.text())
      .then((html) => {
        const $items = this.querySelector('.js-cart-items-body') || null;
        if ($items) {
          $items.innerHTML = html;
          this.refreshCartBubble();
          this.open();
        }
      })
      .catch((error) => console.error({ message: 'Error updating items', error: error }));
  }

  // Refresca el badge del ícono del carrito (.cart-count-bubble) via Section
  // Rendering API. La notificación pop-up ya lo hacía por su cuenta con
  // cart?view=minicart; el drawer no lo hacía, así que el contador no subía
  // al agregar desde el PDP hasta recargar. Lo alineamos aquí.
  refreshCartBubble() {
    return fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return;
        const host = document.getElementById('cart-icon-bubble');
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.shopify-section');
        if (host && fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(() => {});
  }

  // Recupera el drawer completo (header + items + footer con botones) cuando
  // el DOM del drawer perdió la tabla de items (venía de estado vacío o de un
  // estado inconsistente de clases).
  rebuildFromEmpty(openAfter = true) {
    fetch(`${window.Shopify.routes.root}cart?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((html) => {
        const fresh = new DOMParser().parseFromString(html, 'text/html');

        // El interior renderizado del drawer (dentro de #CartDrawer).
        const freshCartDrawer = fresh.querySelector('#CartDrawer');
        const currentCartDrawer = this.querySelector('#CartDrawer');
        if (freshCartDrawer && currentCartDrawer) {
          currentCartDrawer.innerHTML = freshCartDrawer.innerHTML;
        }

        // Sincronizar is-empty del wrapper con el estado REAL del render
        // fresco (no asumir que hay items: si el add falló, el render sigue
        // vacío y el wrapper debe conservar/recuperar is-empty). El
        // cart-drawer-items nuevo ya trae su clase correcta desde el server.
        const hasItems = !!this.querySelector('tr.cart-item');
        this.classList.toggle('is-empty', !hasItems);

        // Re-enganchar listeners que vivían en nodos reemplazados: overlay
        // (click para cerrar) y triggers de modales (.ac-modal-trigger).
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay) overlay.addEventListener('click', this.close.bind(this));
        this.addEvents();

        this.refreshCartBubble();
        if (openAfter) this.open();
      })
      .catch((error) => {
        console.error({ message: 'Error rebuilding cart drawer', error: error });
        // Fallback duro: recargar la vista de items para no dejar el drawer roto.
        fetch(`${window.Shopify.routes.root}cart?view=cart-items`)
          .then((r) => r.text())
          .then((itemsHtml) => {
            const $items = this.querySelector('.js-cart-items-body');
            if ($items) {
              $items.innerHTML = itemsHtml;
              this.classList.remove('is-empty');
              const $di = this.querySelector('cart-drawer-items');
              if ($di) $di.classList.remove('is-empty');
              this.open();
            }
          })
          .catch(() => {});
      });
  }

  addEvents() {
    const $modalTriggers = this.querySelectorAll('.ac-modal-trigger') || [];

    if ($modalTriggers?.length > 0) {
      [...$modalTriggers].forEach(($trigger) => {
        $trigger.addEventListener('click', (event) => {
          this.close();
        });
      });
    }
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
